import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { Circuit, CircuitResume } from '@core/interfaces/circuits.interface'
import { CircuitsService } from '@core/services/circuits.service'
import { KpisStore } from './kpis.store'
import { User, USER_TYPES } from '@core/interfaces/user.interface'

@Injectable({
  providedIn: 'root'
})
export class CircuitsStore {
  private kpisStore = inject(KpisStore)
  private circuitsService = inject(CircuitsService)

  private circuits$ = signal<Circuit[]>([])
  circuits = this.circuits$.asReadonly()

  private circuitsSummaries$ = signal<CircuitResume[]>([])
  circuitsSummaries = this.circuitsSummaries$.asReadonly()

  /**
   * Carga los circuitos para un tipo de usuario específico
   * Si ya están cargados circuitos para ese tipo, no realiza la solicitud
   *
   * @param {USER_TYPES} useType - Tipo de usuario para el cual cargar circuitos
   * @returns {Promise<void>}
   */
  async loadCircuits(useType: USER_TYPES) {
    if (this.checkLoadedCircuitsByUserType(useType)) return

    const category = `circuitos_${useType}`

    const circuits$ = this.circuitsService.fetchCircuits(category, 1)
    const circuits = await firstValueFrom(circuits$)

    this.setCircuits(circuits)
  }

  /**
   * Carga los KPIs para un circuito específico y múltiples usuarios
   * Obtiene las categorías basadas en el rango de fechas del circuito
   *
   * @param {Circuit} circuit - Circuito para el que cargar KPIs
   * @param {User[]} users - Array de usuarios para los que cargar los KPIs
   * @returns {Promise<void>}
   */
  async loadCircuitKpisForUsers(circuit: Circuit, users: User[]) {
    const uids = users.map(user => user.uid)
    await this.kpisStore.loadUsersCircuitKpis(uids, circuit.slug)
  }

  /**
   * Obtiene un circuito por su slug
   *
   * @param {string} slug - Slug del circuito
   * @returns {Circuit | undefined} Circuito encontrado o undefined si no se encuentra
   */
  getCircuitBySlug(slug: string) {
    const circuits = this.circuits$()
    return circuits.find(circuit => circuit.slug === slug)
  }

  /**
   * Verifica si ya hay circuitos cargados para un tipo de usuario específico
   *
   * @param {string} useType - Tipo de usuario a verificar
   * @returns {boolean} true si ya existen circuitos cargados para ese tipo de usuario
   * @private
   */
  private checkLoadedCircuitsByUserType(useType: string) {
    const circuits = this.circuits$()
    return circuits.some(circuit => circuit.user_type === useType)
  }

  /**
   * Establece los circuitos en el store, asegurando que no haya duplicados
   * utilizando un mapa con claves únicas por ID de circuito
   *
   * @param {Circuit[]} circuits - Array de circuitos a almacenar
   */
  setCircuits(circuits: Circuit[]) {
    const existing = this.circuits$()
    const circuitMap = new Map<string, Circuit>()

    existing.forEach(circuit => {
      circuitMap.set(circuit.slug, circuit)
    })

    circuits.forEach(circuit => {
      circuitMap.set(circuit.slug, circuit)
    })

    this.circuits$.set(Array.from(circuitMap.values()))
  }
}
